import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import { useSlotGroup } from '@vibesites/runtime/react';
import { RichTextContent } from '@vibesites/runtime/react';
import { EditableSlotGroup } from '@vibesites/runtime/react';
import { usePropertyField } from '@vibesites/runtime/react';
import { VibesitesImage } from '@vibesites/ui-primitives';
import { expandBedroomRange } from '../utils/bedrooms';

// Static preview fallback for the schema / company-theme views where there's
// no property to expand against — keeps the raw range field span. At runtime
// the component overrides this with the expanded enumeration (see below).
const SUBTITLE_DEFAULT =
  `<p>Be a part of <span data-type="field-token" data-field="property_address_city"></span>'s most exciting luxury apartment community featuring brand new <span data-type="field-token" data-field="property_bedrooms_range"></span> bedroom townhome floor plans.</p>`;

/** Builds the runtime subtitle HTML, expanding the bedroom range into a list
 *  (e.g. "Studio, 1, and 2") while keeping the city as a live field token. */
function buildSubtitleHtml(expandedBeds: string): string {
  return `<p>Be a part of <span data-type="field-token" data-field="property_address_city"></span>'s most exciting luxury apartment community featuring brand new ${expandedBeds} bedroom townhome floor plans.</p>`;
}

const featuredImageDef: SlotGroupDefinition = {
  label: 'Background Image',
  fields: {
    image: {
      label: 'Image',
      type: 'image',
      defaultField: 'property_featured_image',
    },
    alt: {
      label: 'Alt Text',
      type: 'text',
      defaultValue: 'Property hero image',
    },
  },
};

const ctaLinkDef: SlotGroupDefinition = {
  label: 'CTA Link',
  fields: {
    url: {
      label: 'URL',
      type: 'url',
      defaultValue: '/floor-plans',
    },
    label: {
      label: 'Link Text',
      type: 'text',
      defaultValue: 'View Floor Plans',
    },
    _target: {
      label: 'Target',
      type: 'text',
      defaultValue: '_self',
    },
  },
};

export function Hero() {
  const bgImage = useSlotGroup('hero.bg_image', featuredImageDef);
  const ctaLink = useSlotGroup('hero.cta_link', ctaLinkDef);

  // Expand "Studio - 2" → "Studio, 1, and 2" for the subtitle prose. Falls back
  // to the raw-range-span default when the property has no bedroom range.
  const expandedBeds = expandBedroomRange(usePropertyField('property_bedrooms_range'));
  const subtitleDefault = expandedBeds ? buildSubtitleHtml(expandedBeds) : SUBTITLE_DEFAULT;

  return (
    <section className="relative w-full h-[70vh] sm:h-screen overflow-hidden">
      {/* Static background image */}
      <EditableSlotGroup groupPath="hero.bg_image" definition={featuredImageDef}>
        <div className="absolute inset-0">
          {bgImage.image ? (
            <VibesitesImage
              url={bgImage.image}
              alt={bgImage.alt || ''}
              priority
              sizes="100vw"
              className="w-full h-full object-cover"
              style={{
                imageRendering: 'high-quality' as any,
              }}
            />
          ) : (
            <div className="w-full h-full bg-[var(--vibe-color-text)]" />
          )}
        </div>
      </EditableSlotGroup>

      {/* Gradient overlay for text contrast — purely decorative, must not
          intercept clicks (otherwise floating chatbot iframes / widgets
          rendered above the hero can't be clicked through the empty
          gradient area). */}
      <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/35 to-black/20 pointer-events-none" />

      {/* Content. The flex container fills the section (h-full) so children
          can be vertically centered, but we disable pointer events on the
          wrapper itself and re-enable them only on the actual interactive
          children. That way empty space in the hero passes clicks through
          to anything floating on top (e.g. the RentCafe CRM IQ chatbot). */}
      <div className="relative z-10 flex flex-col items-center justify-center h-full text-center text-white px-6 sm:px-8 pointer-events-none">
        <RichTextContent
          path="hero.title"
          label="Title"
          defaultValue='<p><span data-type="field-token" data-field="property_name"></span></p>'
          as="h1"
          className="hero-entrance hero-entrance-2 font-heading text-[60px] leading-[90px] font-normal not-italic text-white mb-5 max-w-5xl drop-shadow-lg pointer-events-auto"
        />

        {/* `capitalize` forces title case — every word renders with its first
            letter uppercased via CSS text-transform, regardless of the
            authored casing. */}
        <RichTextContent
          path="hero.subtitle"
          label="Subtitle"
          defaultValue={subtitleDefault}
          as="p"
          className="hero-entrance hero-entrance-3 font-body text-base sm:text-lg md:text-xl max-w-5xl mb-10 leading-relaxed opacity-90 drop-shadow-md pointer-events-auto capitalize"
        />

        <EditableSlotGroup groupPath="hero.cta_link" definition={ctaLinkDef}>
          <a
            href={ctaLink.url || '/floor-plans'}
            target={ctaLink._target || '_self'}
            className="hero-entrance hero-entrance-4 hero-cta group/cta relative inline-flex items-center justify-center gap-2 px-4 py-2.5 lg:px-6 lg:py-3 font-body text-base font-normal normal-case whitespace-nowrap overflow-hidden transition-all duration-300 pointer-events-auto max-md:w-56"
          >
            <span className="relative z-10">{ctaLink.label || 'View Floor Plans'}</span>
          </a>
        </EditableSlotGroup>
      </div>

      <style>{`
        @keyframes heroEntrance {
          from { opacity: 0; transform: translateY(20px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        .hero-entrance {
          animation: heroEntrance 0.8s cubic-bezier(0.22, 1, 0.36, 1) both;
        }
        .hero-entrance-1 { animation-delay: 0.1s; }
        .hero-entrance-2 { animation-delay: 0.3s; }
        .hero-entrance-3 { animation-delay: 0.55s; }
        .hero-entrance-4 { animation-delay: 0.8s; }
        /* Match Header › schedule_tour_link (.nav-cta-btn): solid primary
           fill, 5px radius, surface fill + white label on hover. */
        .hero-cta {
          background: var(--vibe-color-primary);
          border: 0;
          border-radius: 5px;
          color: #ffffff;
          box-shadow: none;
          transition: background-color 0.25s ease, color 0.25s ease,
            box-shadow 0.25s ease, transform 0.25s ease;
        }
        .hero-cta::before {
          content: '';
          position: absolute;
          inset: 0;
          background: transparent;
          opacity: 0;
          transition: opacity 0.25s ease;
        }
        .hero-cta:hover {
          background: var(--vibe-color-surface);
          color: #ffffff;
          box-shadow:
            0 2px 6px rgba(0,0,0,0.1),
            0 4px 16px color-mix(in srgb, var(--vibe-color-primary) 30%, transparent);
          transform: translateY(-1px);
        }
        .hero-cta:hover::before {
          opacity: 0;
        }
        .hero-cta:active {
          transform: translateY(0);
          box-shadow: none;
        }
        @media (prefers-reduced-motion: reduce) {
          .hero-entrance { animation: none; opacity: 1; transform: none; }
        }
      `}</style>
    </section>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'Hero',
  displayName: 'Hero Section',
  tokens: {
    'hero.title': {
      label: 'Title',
      type: 'richtext',
      defaultValue:
        '<p><span data-type="field-token" data-field="property_name"></span></p>',
    },
    'hero.subtitle': {
      label: 'Subtitle',
      type: 'richtext',
      defaultValue:
        `<p>Be a part of <span data-type="field-token" data-field="property_address_city"></span>'s most exciting luxury apartment community featuring brand new <span data-type="field-token" data-field="property_bedrooms_range"></span> bedroom townhome floor plans.</p>`,
    },
  },
  slotGroups: {
    'hero.bg_image': featuredImageDef,
    'hero.cta_link': ctaLinkDef,
  },
};

(Hero as any).contentSchema = contentSchema;

export default Hero;
